import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { expandCadPlanBbox } from '../utils/cadGeometry';
import { buildCadPlanMapStyle } from '../utils/cadPlanMapStyle';
import {
  getWebGLSupport,
  isWebGLAvailable,
  markWebGLUnavailable,
  reportWebGLUnavailableOnce,
} from '../../Map/utils/webglSupport';
import { cleanupFailedMapContainer } from '../../Map/utils/cleanupFailedMapContainer';
import { isEmbeddedFrame } from '../../Utils/isEmbeddedFrame';

/**
 * MapLibre dedicado al plano CAD (`canvas` / `image`). No reusa `useMapInstance`.
 *
 * @param {{ enabled?: boolean, maxZoom?: number, surface?: string }} [options]
 *   `surface`: para `metadata.surface` del reporte de WebGL.
 */
export function useCadPlanMap({ enabled = true, maxZoom = 22, surface = 'cad_plan' } = {}) {
  const mapInstanceRef = useRef(null);
  const [containerEl, setContainerEl] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  // Sin WebGL no hay plano: el que llama muestra `MapUnavailable` en su lugar.
  const [mapUnavailableReason, setMapUnavailableReason] = useState(() => (
    isWebGLAvailable() ? null : (getWebGLSupport().reason || 'unsupported')
  ));
  const mapRef = useCallback((node) => {
    setContainerEl(node);
  }, []);

  useEffect(() => {
    if (!enabled || !containerEl) return undefined;
    if (!isWebGLAvailable()) {
      const support = getWebGLSupport();
      setMapUnavailableReason(support.reason || 'unsupported');
      reportWebGLUnavailableOnce({ support, surface, embedded: isEmbeddedFrame() });
      return undefined;
    }

    const mapOptions = {
      container: containerEl,
      style: buildCadPlanMapStyle(),
      center: [0, 0],
      zoom: 16,
      minZoom: 0,
      maxZoom: Number.isFinite(maxZoom) ? maxZoom : 22,
      attributionControl: false,
      fadeDuration: 0,
      renderWorldCopies: false,
      pitch: 0,
      bearing: 0,
    };
    let map;
    try {
      map = new maplibregl.Map(mapOptions);
    } catch (error) {
      cleanupFailedMapContainer(containerEl);
      const support = markWebGLUnavailable(error);
      setMapUnavailableReason(support.reason || 'map_init_failed');
      reportWebGLUnavailableOnce({ support, surface, embedded: isEmbeddedFrame() });
      return undefined;
    }
    mapInstanceRef.current = map;

    const markReady = () => {
      map.resize();
      setMapReady(true);
    };
    if (map.isStyleLoaded()) markReady();
    else map.once('load', markReady);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        try { map.resize(); } catch (_) { /* mapa ya destruido */ }
      });
      resizeObserver.observe(containerEl);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      try { map.off('load', markReady); } catch (_) { /* noop */ }
      try { map.remove(); } catch (_) { /* noop */ }
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, [containerEl, enabled, maxZoom, surface]);

  return {
    mapRef,
    mapInstanceRef,
    mapReady,
    mapUnavailable: mapUnavailableReason != null,
    mapUnavailableReason,
  };
}

/**
 * Tope suave: el usuario se mueve libre, pero no se va al infinito.
 *
 * @param {import('maplibre-gl').Map} map
 * @param {[number, number, number, number]|null} bbox
 */
export function applyCadPlanNavigationLimits(map, bbox) {
  if (!map || !bbox) return;
  const expanded = expandCadPlanBbox(bbox);
  if (!expanded) return;
  map.setMaxBounds(new maplibregl.LngLatBounds(
    [expanded[0], expanded[1]],
    [expanded[2], expanded[3]],
  ));
}
